import { useState } from 'react';
import type { Draft } from '../lib/storage';

interface Props {
  drafts: Draft[];
  currentId: string;
  onSwitch: (id: string) => void;
  onCreate: () => void;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
}

export function DraftsBar({ drafts, currentId, onSwitch, onCreate, onDelete, onRename }: Props) {
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (id: string, name: string) => {
    setEditing(id);
    setEditValue(name);
  };
  const finishEdit = () => {
    if (editing && editValue.trim()) onRename(editing, editValue.trim());
    setEditing(null);
  };

  return (
    <div className="rounded-lg border border-tb-border bg-tb-surface p-3 shadow-sm flex items-center gap-2 flex-wrap">
      <span className="text-xs font-semibold text-tb-text2 mr-1">草稿：</span>
      {drafts.map(d => {
        const active = d.id === currentId;
        const isEditing = editing === d.id;
        return (
          <div
            key={d.id}
            className={[
              'inline-flex items-center gap-1 px-2 py-1 rounded text-xs border transition',
              active ? 'bg-tb-sky-bg border-tb-sky-border text-tb-sky' : 'bg-tb-bg border-tb-border text-tb-text hover:bg-tb-surface2',
            ].join(' ')}
          >
            {isEditing ? (
              <input
                autoFocus
                className="bg-tb-surface text-tb-text border border-tb-sky-border rounded px-1 text-xs w-32 outline-none"
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onBlur={finishEdit}
                onKeyDown={e => { if (e.key === 'Enter') finishEdit(); if (e.key === 'Escape') setEditing(null); }}
              />
            ) : (
              <button onClick={() => onSwitch(d.id)} onDoubleClick={() => startEdit(d.id, d.name)} className="font-medium">
                {d.name || '（未命名）'}
                {d.meta.caseNo && <span className="text-tb-text3 ml-1">· {d.meta.caseNo}</span>}
              </button>
            )}
            {active && !isEditing && (
              <>
                <button onClick={() => startEdit(d.id, d.name)} className="text-tb-text3 hover:text-tb-text ml-0.5" title="重新命名" aria-label={`重新命名「${d.name}」`}>✎</button>
                <button
                  onClick={() => {
                    if (confirm(`確定刪除「${d.name}」？此動作無法復原。`)) onDelete(d.id);
                  }}
                  className="text-tb-text3 hover:text-tb-rust"
                  title="刪除"
                  aria-label={`刪除草稿「${d.name}」`}
                >✕</button>
              </>
            )}
          </div>
        );
      })}
      <button
        onClick={onCreate}
        className="text-xs px-2 py-1 rounded border border-dashed border-tb-border text-tb-text2 hover:bg-tb-bg hover:text-tb-text"
      >
        + 新增草稿
      </button>
    </div>
  );
}
